/**
 * The viewer for everything that is not a video or a drawable image (Aziz 2026-09-28):
 * PDFs drawn page by page, XML and other text read in place, audio played, and anything else
 * (ZIP, XLSX, project files) as a clear card with its size and a download. No file
 * opens to "cannot be reviewed" any more. Word documents (2026-10-15) are converted on the
 * server and read in place; a .doc, .odt or .rtf the server cannot convert shows as a card
 * that says so, with its download.
 */
import { useEffect, useState } from 'react';
import { FileName } from '@/components/ui/FileName';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { Download, FileText, FileArchive, File as FileIcon, Loader2 } from 'lucide-react';
import { fetchDocument, type MediaAsset } from '@/lib/queries';
import { showAs, typeBadge, extOf, isJunkFile } from '@/lib/file-types';
import { formatBytes } from '@/lib/upload-center';
import PdfPages from '@/components/creative/PdfPages';

const TEXT_LIMIT = 2 * 1024 * 1024;

/** XML on one line reads as a wall; indent it without touching its content. */
function prettyXml(s: string): string {
  if (!/^\s*</.test(s) || s.split('\n').length > 3) return s;
  let depth = 0;
  return s.replace(/>\s*</g, '>\n<').split('\n').map((line) => {
    if (/^<\//.test(line)) depth = Math.max(0, depth - 1);
    const out = '  '.repeat(depth) + line;
    if (/^<[^!?/][^>]*[^/]>$/.test(line) && !/<\/[^>]+>$/.test(line)) depth += 1;
    return out;
  }).join('\n');
}

/** The reader's page: paper-white like the PDF viewer, each paragraph finding its own
 *  direction (the server marks every block dir="auto"), pictures fitted to the page. */
const DOC_CLS = [
  'doc-reader bg-white text-slate-900 rounded-lg px-5 py-6 sm:px-10 sm:py-8 text-[15px] leading-7 [overflow-wrap:anywhere]',
  '[&_[dir=auto]]:[unicode-bidi:plaintext] [&_p]:my-2 [&_p:empty]:hidden',
  '[&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2',
  '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h4]:font-semibold [&_h4]:mt-3',
  '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ps-6 [&_ol]:ps-6 [&_li]:my-1',
  '[&_a]:text-blue-700 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:my-3 [&_img]:rounded',
  '[&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_td]:border [&_td]:border-slate-300 [&_td]:p-2 [&_th]:border [&_th]:border-slate-300 [&_th]:p-2 [&_th]:bg-slate-100',
  '[&_blockquote]:border-s-4 [&_blockquote]:border-slate-300 [&_blockquote]:ps-3 [&_blockquote]:text-slate-700',
].join(' ');

function DocumentView({ asset, card }: { asset: MediaAsset; card: (note?: string) => JSX.Element }) {
  const q = useQuery({ queryKey: ['document', asset.id], queryFn: () => fetchDocument(asset.id), staleTime: 10 * 60_000, retry: 1 });
  const download = asset.attachment_url || asset.download_url;
  if (q.isLoading) {
    return (
      <div className="flex flex-col items-center gap-2 p-10 text-xs text-white/70" data-testid="file-doc-loading">
        <Loader2 size={22} className="text-accent-400 animate-spin" /> Opening the document...
      </div>
    );
  }
  if (q.isError || !q.data) return card('could not be opened here, download it to read it');
  if (q.data.status !== 'ok' || !q.data.html) {
    return (
      <div data-testid="file-doc-fallback">
        {card()}
        <p className="text-center text-[12px] text-amber-300/90 max-w-[26rem] mx-auto -mt-4 pb-6">{q.data.message ?? 'This document cannot be shown here. Download it to read it.'}</p>
      </div>
    );
  }
  // cleaned on the server with an allow-list; cleaned again here before it reaches the page
  const html = DOMPurify.sanitize(q.data.html, { USE_PROFILES: { html: true }, FORBID_TAGS: ['style', 'form', 'input', 'button'], FORBID_ATTR: ['style'] });
  return (
    <div className="w-[calc(100vw-2rem)] lg:w-[min(960px,calc(100vw-440px))] max-h-[calc(100dvh-200px)] flex flex-col rounded-lg border border-slate-700 bg-slate-950" data-testid="file-doc">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-800 text-[11px] text-white/60">
        <span>{typeBadge(asset)} · {formatBytes(asset.size_bytes ?? 0)}</span>
        {download && <a href={download} className="inline-flex items-center gap-1 text-accent-400 hover:underline"><Download size={12} /> Download</a>}
      </div>
      <div className="flex-1 overflow-auto p-2 sm:p-4">
        <article dir="auto" className={DOC_CLS} data-testid="doc-reader" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}

export default function FileViewer({ asset }: { asset: MediaAsset }) {
  const kind = showAs(asset);
  const url = asset.download_url ?? '';
  const [text, setText] = useState<string | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    setText(null); setErr('');
    if (kind !== 'text' || !url) return;
    if ((asset.size_bytes ?? 0) > TEXT_LIMIT) { setErr('too big'); return; }
    let live = true;
    fetch(url).then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => { if (live) setText(extOf(asset.filename) === 'xml' ? prettyXml(t) : t); })
      .catch(() => { if (live) setErr('failed'); });
    return () => { live = false; };
  }, [kind, url, asset.filename, asset.size_bytes]);

  const download = asset.attachment_url || url;
  const card = (note?: string) => (
    <div className="flex flex-col items-center justify-center gap-3 text-center p-10 min-w-[18rem]" data-testid="file-card">
      <div className="relative text-muted">
        {['zip', 'rar', '7z', 'tar', 'gz'].includes(extOf(asset.filename)) ? <FileArchive size={56} /> : kind === 'text' || kind === 'document' ? <FileText size={56} /> : <FileIcon size={56} />}
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-white">{typeBadge(asset)}</span>
      </div>
      <FileName name={asset.filename} className="block text-sm text-white font-medium break-all max-w-[28rem]" />
      <div className="text-[12px] text-white/60">{formatBytes(asset.size_bytes ?? 0)}{note ? ` · ${note}` : ''}</div>
      {isJunkFile(asset.filename) && (
        <div className="text-[12px] text-amber-300/90 max-w-[26rem]" data-testid="system-file-note">
          A Mac system file, written next to the real file when it is copied. It is not a photo or a clip; the real file is the one without the "._".
        </div>
      )}
      {download && (
        <a href={download} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-accent-500 hover:bg-accent-400 text-white" data-testid="file-download">
          <Download size={13} /> Download
        </a>
      )}
    </div>
  );

  // drawn page by page on every screen: a PDF in a frame is blank on Android and in browsers
  // set to download PDFs, and page 1 only on iPhone (2026-11-01)
  if (kind === 'pdf' && url) return <PdfPages url={url} download={download} />;
  if (kind === 'document') return <DocumentView asset={asset} card={card} />;
  if (kind === 'audio' && url) {
    return (
      <div className="flex flex-col items-center gap-4 p-10" data-testid="file-audio">
        {card()}
        <audio src={url} controls className="w-[min(80vw,480px)]" />
      </div>
    );
  }
  if (kind === 'text') {
    if (err === 'too big') return card('too long to show here');
    if (err) return card('could not be read here');
    if (text === null) return <Loader2 size={22} className="text-accent-400 animate-spin m-10" />;
    return (
      <div className="w-[calc(100vw-2rem)] lg:w-[min(960px,calc(100vw-440px))] max-h-[calc(100dvh-200px)] flex flex-col rounded-lg border border-slate-700 bg-slate-950" data-testid="file-text">
        <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-800 text-[11px] text-white/60">
          <span>{typeBadge(asset)} · {formatBytes(asset.size_bytes ?? 0)}</span>
          {download && <a href={download} className="inline-flex items-center gap-1 text-accent-400 hover:underline"><Download size={12} /> Download</a>}
        </div>
        <pre dir="auto" className="flex-1 overflow-auto p-3 text-[12px] leading-5 text-slate-200 whitespace-pre-wrap break-words font-mono">{text}</pre>
      </div>
    );
  }
  return card();
}
